import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ViewerPrimaryColorUserStyle } from '../UserCustomization';
import MenuCloseButton from './MenuCloseButton';
import svgInline from '../SvgInline/SvgInline';
import refreshIcon from '../../shared/assets/icon-refresh.svg';
import { railroads, storage } from '../../config';
import {
  activateMapLoadingStatus,
  deactivateMapLoadingStatus,
  restorePredefinedMapLayers,
  setCustomLayer,
  setPredefinedLayer,
} from '../../store/actions';

import './MapLayersMenu.css';

// "Railroads" can be added below, but was disabled for performance reasons
const predefinedLayers = ['Hospitals', 'Universities', 'Airports', ...Object.keys(railroads)];
const airportIcon = `${storage.cloudStoragePath}/share/marker-airport.png`;
const hospitalIcon = `${storage.cloudStoragePath}/share/marker-hospital.png`;
const universityIcon = `${storage.cloudStoragePath}/share/marker-college.png`;

const MapLayersMenu = ({
  offsetLeft,
  onCloseClick,
  customLayers,
  toggleCustomLayer,
  togglePredefinedLayer,
  isPredefinedLayerEnabled,
  activeLayers,
  type,
  reset,
  mapLoading,
}) => {
  const predefinedLayerLabel = label => {
    if (label === 'Iowa Interstate') {
      return `${label} Railroad`;
    } else if (Object.keys(railroads).includes(label)) {
      return `${label} Railway`;
    }
    return label;
  };
  return (
    <div className="map-toolbar__menu map-toolbar--map-layers" style={{ left: offsetLeft }}>
      <MenuCloseButton onCloseClick={onCloseClick} />
      <div className="toolbox-content filter-box map-layer-menu-wrapper">
        <h4>Select Layers:</h4>
        <div className={mapLoading ? 'map-layer-menu loading' : 'map-layer-menu'}>
          {customLayers.map(customLayer => (
            <div className="map-layer-menu-item" key={customLayer.id}>
              <input
                disabled={mapLoading}
                type="checkbox"
                id={`custom-layer-${customLayer.id}`}
                checked={(activeLayers.get('customLayer') || []).some(l => l.id === customLayer.id)}
                onChange={e => {
                  toggleCustomLayer(activeLayers, customLayer, e.target.checked, type);
                }}
              />
              <label className="map-layer-label" htmlFor={`custom-layer-${customLayer.id}`}>
                <div className="layer-swatch" style={{ background: customLayer.fillColor, opacity: customLayer.fillOpacity, border: `1px solid ${customLayer.lineColor}` }}>&nbsp;</div>
                <div>{customLayer.title}</div>
              </label>
            </div>
          ))}

          {predefinedLayers.map(predefinedLayer => (
            isPredefinedLayerEnabled(predefinedLayer) ?
              <div className="map-layer-menu-item" key={predefinedLayer}>
                <input
                  disabled={mapLoading}
                  type="checkbox"
                  id={`predefined-layer-${predefinedLayer}`}
                  checked={(activeLayers.get('predefinedLayer') || []).includes(predefinedLayer)}
                  onChange={e => {
                    togglePredefinedLayer(activeLayers, predefinedLayer, e.target.checked, type);
                  }}
                />
                <label className="map-layer-label" htmlFor={`predefined-layer-${predefinedLayer}`}>
                  {!['Hospitals', 'Universities', 'Airports'].includes(predefinedLayer) &&
                    <div className="layer-swatch railroad" style={{ background: railroads[predefinedLayer].color }}>&nbsp;</div>
                  }
                  {predefinedLayer === 'Airports' &&
                    <div className="layer-swatch"><img src={airportIcon} alt="" /></div>
                  }
                  {predefinedLayer === 'Hospitals' &&
                    <div className="layer-swatch"><img src={hospitalIcon} alt="" /></div>
                  }
                  {predefinedLayer === 'Universities' &&
                    <div className="layer-swatch"><img src={universityIcon} alt="" /></div>
                  }
                  <div>{predefinedLayerLabel(predefinedLayer)}</div>
                </label>
              </div>
            : null
          ))}
        </div>
        <div className="map-layer-menu-buttons">
          <ViewerPrimaryColorUserStyle domStyle="backgroundColor">
            <a
              className="btn"
              onClick={onCloseClick}
              tabIndex="0"
            >
              Close
            </a>
          </ViewerPrimaryColorUserStyle>
          <ViewerPrimaryColorUserStyle domStyle="backgroundColor">
            <a
              className={mapLoading ? 'btn loading' : 'btn'}
              onClick={() => { reset(type); }}
              tabIndex="0"
            >
              Reset {mapLoading && svgInline(refreshIcon)}
            </a>
          </ViewerPrimaryColorUserStyle>
        </div>
      </div>
    </div>
  );
};

MapLayersMenu.propTypes = {
  offsetLeft: PropTypes.number,
  onCloseClick: PropTypes.func.isRequired,
  customLayers: PropTypes.arrayOf(PropTypes.object),
  type: PropTypes.string.isRequired,
  toggleCustomLayer: PropTypes.func,
  togglePredefinedLayer: PropTypes.func,
  isPredefinedLayerEnabled: PropTypes.func,
  activeLayers: PropTypes.object,
  reset: PropTypes.func,
  mapLoading: PropTypes.bool,
};

const mapStateToProps = (state, ownProps) => ({
  // For some reason, we get a whole lot of duplicated layers sometimes.
  // This will return an array that is unique by id.
  customLayers: ((state.viewer.layers || {}).custom || [])
    .filter((layer, pos, arr) => {
      return (arr.findIndex(_layer => _layer.id === layer.id) === pos) && (!layer.showByDefault);
    },
  ),
  activeLayers: state[ownProps.type].mapLayers,
  isPredefinedLayerEnabled: layer => {
    if (state.viewer.layers && state.viewer.layers.default) {
      if (railroads[layer] && railroads[layer].defaultOff) {
        if (state.viewer.layers.default[layer.replace(' ', '').toLowerCase()] === undefined) {
          return false;
        }
      }
      return state.viewer.layers.default[layer.replace(' ', '').toLowerCase()] !== 0;
    }
    if (railroads[layer] && railroads[layer].defaultOff) {
      return false;
    }
    return true;
  },
  mapLoading: state.mapLoading,
  viewerPolygon: state.viewer.polygon,
  viewport: state.viewportInfo,
});

const mapDispatchToProps = dispatch => ({
  toggleCustomLayer: (activeLayers, layer, enabled, type) => {
    dispatch(activateMapLoadingStatus());
    setTimeout(() => {
      const activeCustomLayers = activeLayers.get('customLayer') || [];
      const nextLayers = enabled
        ? [...activeCustomLayers, layer]
        : activeCustomLayers.filter(l => l.id !== layer.id);
      dispatch(setCustomLayer(type, nextLayers));
      dispatch(deactivateMapLoadingStatus());
    }, 200);
  },

  togglePredefinedLayer: (activeLayers, layer, enabled, type) => {
    // if layer not railroad or is being toggled off we start/stop loading indicators here,
    // otherwise Predefined.js sets loading indicator status
    if (enabled || !['Hospitals', 'Universities', 'Airports'].includes(layer)) {
      const activePredefinedLayers = activeLayers.get('predefinedLayer') || [];
      const nextLayers = enabled
        ? [...activePredefinedLayers, layer]
        : activePredefinedLayers.filter(l => l !== layer);
      dispatch(setPredefinedLayer(type, nextLayers));
    } else {
      dispatch(activateMapLoadingStatus());
      setTimeout(() => {
        const activePredefinedLayers = activeLayers.get('predefinedLayer') || [];
        const nextLayers = enabled
          ? [...activePredefinedLayers, layer]
          : activePredefinedLayers.filter(l => l !== layer);
        dispatch(setPredefinedLayer(type, nextLayers));
        dispatch(deactivateMapLoadingStatus());
      }, 200);
    }
  },

  reset: type => {
    dispatch(activateMapLoadingStatus());
    setTimeout(() => {
      dispatch(setCustomLayer(type, []));
      dispatch(restorePredefinedMapLayers(type));
      dispatch(deactivateMapLoadingStatus());
    }, 200);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(MapLayersMenu);



// WEBPACK FOOTER //
// ./src/components/MapToolbar/MapLayersMenu.js